import { useState } from 'react';
import type { FormEvent } from 'react';
import { useClusterApi } from '../../api/ClusterApiProvider';
import { RESOURCE_KINDS } from '../../api/types';
import type { Resource, ResourceKind } from '../../api/types';
import type { ApiError } from '../../api/errors';
import { useI18n } from '../../i18n/useI18n';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { AttributionNotice } from '../../ui/AttributionNotice';
import { Button } from '../../ui/primitives';
import { SelectField, TextAreaField, TextField } from '../../ui/form';
import { Drawer } from '../../ui/Drawer';
import { Modal } from '../../ui/Modal';
import { KIND_LABEL } from '../../ui/ResourceTable';
import { writeResource } from './catalogWrite';
import type { WriteOutcome } from './catalogWrite';
import {
  answersOnItsOwnDomain,
  checkResource,
  emptyResourceForm,
  formForOffer,
  formForResource,
  needsPort,
  servesToolDescriptors,
  toResourceInput,
} from './resourceForm';
import type { ResourceErrors, ResourceField, ResourceForm } from './resourceForm';

/**
 * Declaring a resource, or rewriting one.
 *
 * One dialog for both, because on the wire it is one call: a write says "make
 * the resource called this look like that", and running it a second time with
 * the same body is the same as running it once. That is also why every field is
 * filled in when an existing resource is opened — the write replaces the row
 * whole, so a form that showed only what somebody wanted to change would drop
 * everything it did not show.
 *
 * The id is the address written to and is fixed once the resource exists.
 * Renaming would be declaring a second resource and removing the first, and
 * removing one closes access to it — not something to do behind a text field
 * somebody edited by accident.
 */
export function ResourceDialog({
  resource,
  offer,
  kinds = RESOURCE_KINDS,
  actorName,
  onClose,
  onWritten,
  surface = 'modal',
}: {
  /** Absent when this is a new resource. */
  resource?: Resource;
  /**
   * What a connector offered, when the dialog was opened from that section.
   *
   * It fills the id and the kind and nothing else — those two the connector
   * stated, the rest is the operator's decision. The resource is still new:
   * the id stays editable, because a connector's word about a name is not a
   * reason to take it.
   */
  offer?: { resource_id: string; kind: string };
  /**
   * The kinds this tenancy's own build understands, as its session named them.
   *
   * Passed in rather than read from the module's list because the answer is the
   * server's: a kind that arrived in a release after this tenancy's build is one
   * its cluster would refuse by name, and a select that offered it would be
   * laying out a refusal. Absent falls back on everything this console knows.
   */
  kinds?: readonly ResourceKind[];
  actorName: string;
  onClose: () => void;
  onWritten: (id: string, outcome: WriteOutcome) => void;
  /**
   * Which shell the same form is wearing.
   *
   * Editing an existing resource happens **in the drawer that was showing it**:
   * a panel that opens a dialog on top of itself makes somebody read the same
   * row twice, in two places, and decide which one is the truth. Declaring a
   * new one has no drawer to be in, so it is a modal.
   */
  surface?: 'modal' | 'drawer';
}) {
  const { t } = useI18n();
  const api = useClusterApi();

  const existing = resource !== undefined;
  const [form, setForm] = useState<ResourceForm>(() => {
    if (resource !== undefined) return formForResource(resource);
    return offer === undefined ? emptyResourceForm() : formForOffer(offer);
  });
  const [errors, setErrors] = useState<Partial<Record<ResourceField, string>>>({});
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (field: ResourceField) => (event: { target: { value: string } }) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  async function onSubmit(event: FormEvent) {
    event.preventDefault();

    const found: ResourceErrors = checkResource(form);
    const shown: Partial<Record<ResourceField, string>> = {};
    for (const field of Object.keys(found) as ResourceField[]) {
      const key = found[field];
      if (key !== undefined) shown[field] = t(key);
    }
    setErrors(shown);
    setFailure(null);
    if (Object.keys(shown).length > 0) return;

    const id = form.id.trim();
    setSubmitting(true);
    const outcome = await writeResource(api, id, toResourceInput(form));
    setSubmitting(false);

    if (outcome.done === 'refused') {
      // The server names the box; the console never guesses it from a code.
      const named = fieldsOf(outcome.error);
      if (Object.keys(named).length > 0) setErrors(named);
      setFailure(outcome.error);
      return;
    }
    onWritten(id, outcome);
  }

  const Surface = surface === 'drawer' ? Drawer : Modal;

  return (
    <Surface
      title={t(existing ? 'resource.edit.title' : 'resource.new.title')}
      subtitle={t('resource.subtitle')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <span className="spacer" />
          <Button variant="primary" form="resource" type="submit" disabled={submitting}>
            {submitting ? `${t('resource.submitting')}…` : t('resource.submit')}
          </Button>
        </>
      }
    >
      {failure !== null && (
        <div style={{ marginBottom: 18 }}>
          <WriteFailure failure={failure} />
        </div>
      )}

      <form id="resource" onSubmit={(event) => void onSubmit(event)} noValidate>
        <TextField
          label={t('resource.field.id')}
          hint={t(existing ? 'resource.id.fixed' : 'resource.id.hint')}
          placeholder={t('resource.id.placeholder')}
          mono
          readOnly={existing}
          value={form.id}
          onChange={set('id')}
          {...(errors.id === undefined ? {} : { error: errors.id })}
        />

        <SelectField
          label={t('resource.field.kind')}
          hint={t('resource.kind.hint')}
          value={form.kind}
          onChange={(event) => {
            setForm((current) => ({ ...current, kind: event.target.value as ResourceKind }));
          }}
        >
          {kinds.map((kind) => (
            <option key={kind} value={kind}>
              {t(KIND_LABEL[kind])}
            </option>
          ))}
        </SelectField>

        <TextField
          label={t('resource.field.project')}
          hint={t('resource.project.hint')}
          mono
          value={form.project}
          onChange={set('project')}
          {...(errors.project === undefined ? {} : { error: errors.project })}
        />
        <TextField
          label={t('resource.field.environment')}
          hint={t('resource.environment.hint')}
          mono
          value={form.environment}
          onChange={set('environment')}
          {...(errors.environment === undefined ? {} : { error: errors.environment })}
        />
        <TextField
          label={t('resource.field.hostname')}
          hint={t('resource.hostname.hint')}
          mono
          value={form.hostname}
          onChange={set('hostname')}
          {...(errors.hostname === undefined ? {} : { error: errors.hostname })}
        />
        <TextField
          label={t('resource.field.port')}
          hint={t(needsPort(form.kind) ? 'resource.port.required' : 'resource.port.hint')}
          mono
          inputMode="numeric"
          value={form.port}
          onChange={set('port')}
          {...(errors.port === undefined ? {} : { error: errors.port })}
        />
        {/* Only an HTTP application answers on a domain of its own, so the box
            is shown for that kind alone — and, like the pin below, sent for no
            other. A field that appeared everywhere would be asking eight kinds
            a question none of them has. */}
        {answersOnItsOwnDomain(form.kind) && (
          <TextField
            label={t('resource.field.nativeName')}
            hint={t('resource.nativeName.hint')}
            placeholder={t('resource.nativeName.placeholder')}
            mono
            value={form.native_name}
            onChange={set('native_name')}
            {...(errors.native_name === undefined ? {} : { error: errors.native_name })}
          />
        )}
        <TextField
          label={t('resource.field.aliases')}
          hint={t('resource.aliases.hint')}
          mono
          value={form.aliases}
          onChange={set('aliases')}
          {...(errors.aliases === undefined ? {} : { error: errors.aliases })}
        />
        <TextField
          label={t('resource.field.parent')}
          hint={t('resource.parent.hint')}
          mono
          value={form.parent}
          onChange={set('parent')}
          {...(errors.parent === undefined ? {} : { error: errors.parent })}
        />
        <TextField
          label={t('resource.field.approvers')}
          hint={t('resource.approvers.hint')}
          mono
          inputMode="numeric"
          value={form.min_approvers}
          onChange={set('min_approvers')}
          {...(errors.min_approvers === undefined ? {} : { error: errors.min_approvers })}
        />
        {servesToolDescriptors(form.kind) && (
          <fieldset>
            <legend className="fieldset-legend">{t('resource.field.pin')}</legend>
            <label className="alg">
              <input
                type="checkbox"
                checked={form.pin_tool_descriptors}
                onChange={(event) => {
                  const pinned = event.target.checked;
                  setForm((current) => ({ ...current, pin_tool_descriptors: pinned }));
                }}
              />
              <span>{t('resource.pin.label')}</span>
            </label>
            <p className="hint">{t('resource.pin.hint')}</p>
          </fieldset>
        )}
        <TextAreaField
          label={t('resource.field.labels')}
          hint={t('resource.labels.hint')}
          mono
          rows={4}
          value={form.labels}
          onChange={set('labels')}
          {...(errors.labels === undefined ? {} : { error: errors.labels })}
        />
      </form>

      <div className="stack" style={{ marginTop: 18 }}>
        <Alert tone="neutral" title={t('resource.whole.title')}>
          {/* The first is about the form in front of somebody: leaving a box
              empty takes the value off the resource. The second is how a write
              travels, which is the same on every write and waits. */}
          <p>{t('resource.whole.body')}</p>
        </Alert>
        <AttributionNotice actorName={actorName} />
      </div>
    </Surface>
  );
}

/** The server's complaints, keyed by the box they are about. */
function fieldsOf(failure: ApiError): Partial<Record<ResourceField, string>> {
  const named: Partial<Record<ResourceField, string>> = {};
  const fields: ResourceField[] = [
    'id',
    'kind',
    'project',
    'environment',
    'hostname',
    'port',
    'aliases',
    'parent',
    'min_approvers',
    'labels',
    'native_name',
  ];
  for (const field of fields) {
    const reason = failure.fieldError(field);
    if (reason !== undefined) named[field] = reason;
  }
  return named;
}

/**
 * The refusals a write meets, and two of them are not "something went wrong".
 *
 * A name the cluster will not record stops the write at our edge with nothing
 * touched on either side, and it is fixed at the tenancy's identity provider
 * rather than here. A policy that no longer names this person an administrator
 * is the same kind of answer: correct, and not about the resource.
 */
function WriteFailure({ failure }: { failure: ApiError }) {
  const { t } = useI18n();

  if (failure.code === 'operator_not_nameable') {
    return (
      <Alert tone="deny" title={t('resource.notNameable.title')} live>
        <p>{t('resource.notNameable.body')}</p>
        <p style={{ marginTop: 8 }}>{t('resource.notNameable.fix')}</p>
      </Alert>
    );
  }
  if (failure.code === 'forbidden') {
    return (
      <Alert tone="deny" title={t('resource.forbidden.title')} live>
        {t('resource.forbidden.body')}
      </Alert>
    );
  }
  return <ErrorAlert error={failure} />;
}
